import { supabase } from "./supabase";
import type { ScanProgressEvent } from "../types";

const IGNORED_NAMES = new Set([
  ".Trashes",
  ".Spotlight-V100",
  ".fseventsd",
  ".DocumentRevisions-V100",
  ".TemporaryItems",
  ".com.apple.timemachine.donotpresent",
  ".DS_Store",
  ".AppleDouble",
  "$RECYCLE.BIN",
  "System Volume Information",
]);

const BATCH_SIZE = 500;
const PROGRESS_THROTTLE_MS = 200;

interface FileRow {
  disk_id: string;
  path: string;
  name: string;
  parent_path: string | null;
  is_directory: boolean;
  extension: string | null;
  size_bytes: number | null;
  created_at_fs: string | null;
  modified_at_fs: string | null;
}

export interface ScanResult {
  filesScanned: number;
  directoriesScanned: number;
  bytesScanned: number;
  durationMs: number;
}

export function isFileSystemAccessSupported(): boolean {
  return typeof window !== "undefined" && "showDirectoryPicker" in window;
}

export async function pickDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (!isFileSystemAccessSupported()) {
    throw new Error(
      "Ton navigateur ne supporte pas le scan local. Ouvre l'app dans Chrome ou Edge pour indexer un disque."
    );
  }
  try {
    return await window.showDirectoryPicker({ mode: "read", id: "koloc-disk-scan" });
  } catch (err) {
    if ((err as DOMException).name === "AbortError") return null;
    throw err;
  }
}

export class BrowserScanner {
  private cancelled = false;
  private filesScanned = 0;
  private dirsScanned = 0;
  private bytesScanned = 0;
  private buffer: FileRow[] = [];
  private recentPaths: string[] = [];
  private lastEmit = 0;

  constructor(
    private readonly diskId: string,
    private readonly rootHandle: FileSystemDirectoryHandle,
    private readonly onProgress: (e: ScanProgressEvent) => void
  ) {}

  cancel(): void {
    this.cancelled = true;
  }

  isCancelled(): boolean {
    return this.cancelled;
  }

  async run(): Promise<ScanResult> {
    const t0 = Date.now();
    this.emit(true);
    await this.walk(this.rootHandle, "");
    if (!this.cancelled) {
      await this.flush();
    }
    return {
      filesScanned: this.filesScanned,
      directoriesScanned: this.dirsScanned,
      bytesScanned: this.bytesScanned,
      durationMs: Date.now() - t0,
    };
  }

  private async walk(handle: FileSystemDirectoryHandle, basePath: string): Promise<void> {
    if (this.cancelled) return;

    const entries: Array<[string, FileSystemHandle]> = [];
    try {
      for await (const entry of handle.entries()) {
        entries.push(entry);
      }
    } catch {
      return;
    }

    for (const [rawName, child] of entries) {
      if (this.cancelled) return;
      if (IGNORED_NAMES.has(rawName)) continue;

      const name = rawName.normalize("NFC");
      const path = basePath ? `${basePath}/${name}` : name;
      const parentPath = basePath || null;

      if (child.kind === "directory") {
        this.dirsScanned++;
        this.buffer.push({
          disk_id: this.diskId,
          path,
          name,
          parent_path: parentPath,
          is_directory: true,
          extension: null,
          size_bytes: null,
          created_at_fs: null,
          modified_at_fs: null,
        });
        this.emit(false, path);
        await this.maybeFlush();
        await this.walk(child as FileSystemDirectoryHandle, path);
      } else if (child.kind === "file") {
        try {
          const file = await (child as FileSystemFileHandle).getFile();
          const dot = name.lastIndexOf(".");
          const ext = dot > 0 && dot < name.length - 1
            ? name.slice(dot + 1).toLowerCase().normalize("NFC")
            : null;

          this.buffer.push({
            disk_id: this.diskId,
            path,
            name,
            parent_path: parentPath,
            is_directory: false,
            extension: ext,
            size_bytes: file.size,
            created_at_fs: null,
            modified_at_fs: new Date(file.lastModified).toISOString(),
          });
          this.filesScanned++;
          this.bytesScanned += file.size;
          this.emit(false, path);
          await this.maybeFlush();
        } catch {
          // permission denied / file vanished, skip
        }
      }
    }
  }

  private async maybeFlush(): Promise<void> {
    if (this.buffer.length >= BATCH_SIZE) await this.flush();
  }

  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return;
    const chunk = this.buffer;
    this.buffer = [];
    const { error } = await supabase.from("files").insert(chunk);
    if (error) {
      throw new Error(`Insert files: ${error.message}`);
    }
  }

  private emit(force: boolean, currentPath?: string): void {
    if (currentPath) {
      this.recentPaths.unshift(currentPath);
      if (this.recentPaths.length > 5) this.recentPaths.pop();
    }
    const now = Date.now();
    if (!force && now - this.lastEmit < PROGRESS_THROTTLE_MS) return;
    this.lastEmit = now;
    this.onProgress({
      filesScanned: this.filesScanned,
      directoriesScanned: this.dirsScanned,
      bytesScanned: this.bytesScanned,
      currentPath: currentPath ?? null,
      recentPaths: [...this.recentPaths],
    });
  }
}
